﻿import "ol/ol.css";
import "ol-ext/dist/ol-ext.css";
import ProjectionHandler from './handlers/ProjectionHandler';
import { addOption, getResolutionForScale, getScaleForResolution } from './utils/utils';
import View from 'ol/View';
import { getCenter as getExtentCenter, createEmpty as createEmptyExtent, extend as extendExtent, extend } from 'ol/extent';
import Map from 'ol/Map';
import LayerHandler from './handlers/LayerHandler';
import SelectionHandler from './handlers/SelectionHandler';
import GeolocationHandler from './handlers/GeolocationHandler';
import GeneralActionHandler from '../ui/GeneralActionHandler';
import FeatureActionHandler from '../ui/FeatureActionHandler';
import * as eventTypes from './utils/eventTypes';
import * as defaults from './utils/defaults';
import ResourcesHandler from './handlers/ResourcesHandler';
import GeneralSettingsHandler from './handlers/GeneralSettingsHandler';
import GeneralActionSettingsHandler from './handlers/GeneralActionSettingsHandler';
import LayerSettingsHandler from './handlers/LayerSettingsHandler';
import GisSettingsHandler from './handlers/GisSettingsHandler';
import TemporaryEditHandler from './handlers/TemporaryEditHandler';
import { transformExtent, transform, getPointResolution } from 'ol/proj';
import FeatureHandler from "./handlers/FeatureHandler";
import { bbox as bboxStrategy, all as allStrategy } from 'ol/loadingstrategy';
import TileQueue from 'ol/TileQueue';
import * as layerTypes from './utils/layerTypes';
import * as geomTypes from './utils/geomTypes';
import { createOlStyleFunction } from './utils/style/canvas/OlStyler';
import * as formats from './utils/formats';
import WKT from 'ol/format/WKT';
import * as styleTypes from './utils/style/styleTypes';
export default class MapControl {
    constructor(mapId, container, rootURL, classId) { 
        this.mapId = mapId;
        this.container = container;
        this.rootURL = rootURL;
        this.resourcesHandler = null;
        this.generalSettingsHandler = null;
        this.layerSettingsHandler = null;
        this.generalActionSettingsHandler = null;
        this.gisSettingsHandler = null;
        this.featureHandler = null;
        this.projectionHandler = new ProjectionHandler(rootURL);
        this.generalActionHandler = new GeneralActionHandler();
        this.featureActionHandler = null;
        this.gisSettingsHandler = null;
        this.map = null;
        this.layerHandler = null;
        this.selectionHandler = null;
        this.temporaryEditHandler = null;
        this.classId = classId != null ? classId : this.mapId;
        this.mapElementId = 'xpmap-' + this.mapId + '-' + this.classId;
        this.loaded = false;
        this._clearEvents();
    }

    parseWktFeatures(features, geomField, idField, srid, layerId) {
        return this.layerHandler._parseWktFeatures(features, geomField, idField, srid, layerId);
    }

    getLayerSettingsByEntityId(id) {
        return this.layerSettingsHandler.getLayerSettingsByEntityId(id);
    }

    showSearchResultBBox(bbox, lat, lon, styleId, minScale) {
        const view = this.map.getView();
        const size = this.map.getSize();
        let padding = [40, 40, 40, 40];
        let extraUnits = 0;
        let pointWkt = 'POINT(' + lon + ' ' + lat + ')';
        let wkt = this.nominatimBboxToWkt(bbox.toJSON());
        let format = new WKT();
        let mapProjection = this.generalSettingsHandler.settings.ProjCode;
        var bboxFeature = format.readFeature(wkt, {
            dataProjection: 'EPSG:4326',
            featureProjection: mapProjection
        });

        var pointFeature = format.readFeature(pointWkt, {
            dataProjection: 'EPSG:4326',
            featureProjection: mapProjection
        });

        const [cx, cy] = pointFeature.getGeometry().getCoordinates();
        let [minx, miny, maxx, maxy] = bboxFeature.getGeometry().getExtent();

        const dx = Math.max(cx - minx, maxx - cx, 1e-9) + extraUnits;
        const dy = Math.max(cy - miny, maxy - cy, 1e-9) + extraUnits;

        const availW = Math.max(1, size[0] - (padding[1] + padding[3]));
        const availH = Math.max(1, size[1] - (padding[0] + padding[2]));

        const resX = (2 * dx) / availW;
        const resY = (2 * dy) / availH;
        const targetResolution = Math.max(resX, resY);

        minScale = getScaleForResolution(targetResolution, this.generalSettingsHandler.settings.ProjCode);

        let convertedWkt = format.writeFeature(pointFeature);
        this.showSearchResult(convertedWkt, styleId, minScale);
    }

    nominatimBboxToWkt(bbox) {
        if (!Array.isArray(bbox) || bbox.length !== 4) {
            throw new Error('bbox must be an array: [south, north, west, east]');
        }
        const [south, north, west, east] = bbox.map(Number);
        if ([south, north, west, east].some(Number.isNaN)) {
            throw new Error('bbox values must be numeric or numeric strings');
        }
        return `POLYGON((${west} ${south}, ${east} ${south}, ${east} ${north}, ${west} ${north}, ${west} ${south}))`;
    }

    showSearchResult(wkt, styleId, minScale) {
        if (wkt != null) {
            let layer = this.layerHandler.getLayerById(layerTypes.SEARCH)[0];

            this.layerHandler.styleHandler.loadStyle((styleId == null ? styleTypes.SYSTEM_STYLE_ALL : styleId), (styleId == null ? true : false)).then(() => {
                if (styleId == null) {
                    styleId = 'sys_' + styleTypes.SYSTEM_STYLE_ALL;
                }

                let style = this.layerHandler.styleHandler.getCombinedStyle(styleId, null, null);

                let styleFunction = (feature) => {
                    let styleGetter = createOlStyleFunction(style.style, this.map);
                    return styleGetter(feature);
                }

                layer.setStyle(styleFunction);

                this.addTemporaryFeatures(layerTypes.SEARCH, [{ id: 1, geom: wkt }], formats.WKT, true, true);
            });
        } else {
            let layer = this.layerHandler.getLayerById(layerTypes.SEARCH)[0];
            layer.getSource().clear();
        }
    }

    loadExternalLayer(id) {
        this.layerSettingsHandler.loadExternalLayer(id);
    }

    removeExternalLayer(id) {
        this.layerSettingsHandler.removeExternalLayer(id);
    }

    refresh() {
        this.layerHandler.refresh();
    }

    fitMap() {
        this.map.getView().fit(this._getMapDefaultExtent(), { duration: 1000, padding: [50, 50, 50, 50] });
    }

    zoomIn() {
        this.map.getView().animate({
            zoom: this.map.getView().getZoom() + 1,
            duration: 100
        })
    }

    zoomOut() {
        this.map.getView().animate({
            zoom: this.map.getView().getZoom() - 1,
            duration: 100
        })
    }

    getEntityLayersDescriptors() {
        return this.layerHandler.getEntityLayersDescriptors();
    }

    showSelectedFeatures(layerId) {
        var selectedFeatures = this.getAllSelectedFeatureDetails(layerId);
        var extent = createEmptyExtent();

        for (var i = 0; i < selectedFeatures.length; i++) {
            var geom = selectedFeatures[i].getGeometry();

            if (geom != null && geom.getExtent() != null) {
                extendExtent(extent, geom.getExtent());
            }
        }

        this.map.getView().fit(extent, { duration: 1000, padding: [50, 50, 50, 50] });
    }

    getFeature(layerId, featureId) {
        try {
            var layer = this.layerHandler.getLayerById(layerId)[0];
            var feature = null;
            if (layer.getSource().strategy_ == allStrategy) {
                var idField = layer.get("idField");
                var features = layer.getSource().getFeatures();
                for (var i = 0; i < features.length && feature == null; i++) {
                    if (features[i].get(idField) == featureId) {
                        feature = features[i];
                        break;
                    }
                }

                if (feature == null) {
                    throw "No feature found";
                }
            }
            else {
                var idField = layer.get("idField");
                var features = layer.getSource().getFeatures();
                for (var i = 0; i < features.length && feature == null; i++) {
                    if (features[i].get(idField) == featureId) {
                        feature = features[i];
                        break;
                    }
                }

                if (feature == null) {
                    feature = this.selectionHandler.getSelectedFeatureDetails(layerId, featureId);
                }

                if (feature == null) {
                    throw "No feature found";
                }
            }

            return feature;
        } catch (err) {
            return null;
        }
    }

    highlightFeature(layerId, featureId) {
        let feature = this.getFeature(layerId, featureId);

        let oldState = feature.get('xstate');

        if (feature != null) {
            feature.set('xstate', 1);
        }

        return oldState;
    }

    unhighlightFeature(layerId, featureId, oldState) {
        let feature = this.getFeature(layerId, featureId);

        if (oldState == null) {
            oldState = 0;
        }

        if (feature != null) {
            feature.set('xstate', oldState);
        }
    }

    getSelectedFeatures() {
        var response = this.selectionHandler.getSelectedFeatures();
        if (response == null || response.layerId == null) {
            return null;
        }

        var layer = this.layerSettingsHandler.getLayerSettings(response.layerId);
        var features = [];
        for (var i = 0; i < response.selectedFeatures.length; i++) {
            var featureWKT = (new WKT()).writeGeometry(response.selectedFeatures[i].getGeometry());
            var geometryName = response.selectedFeatures[0].getGeometryName();
            var feature = JSON.parse(JSON.stringify(response.selectedFeatures[0].getProperties()));
            feature[geometryName] = featureWKT;
            features.push(feature);
        }

        return {
            layerId: response.layerId,
            layerName: layer.Title,
            selectedFeatures: features
        }
    }

    showFeature(layerId, featureId, scale, onSuccessCallback, onErrorCallback) {

        try {
            $event.notify(eventTypes.LAYER_VISIBILITY_CHANGED, { layerId: layerId, visible: true }, this, this.classId);
            var layer = this.layerHandler.getLayerById(layerId)[0];
            var feature = null;
            if (layer.getSource().strategy_ == allStrategy) {
                var idField = layer.get("idField");
                var features = layer.getSource().getFeatures();
                for (var i = 0; i < features.length && feature == null; i++) {
                    if (features[i].get(idField) == featureId) {
                        feature = features[i];
                        break;
                    }
                }

                if (feature == null) {
                    throw "No feature found";
                }
                this.selectionHandler.select(layerId, featureId, true, false, true);
            }
            else {
                this.refreshLayer(layerId);
                this.selectionHandler.select(layerId, featureId, true, true, true);
                feature = this.selectionHandler.getSelectedFeatureDetails(layerId, featureId);

                this.selectionHandler.notifySelection(featureId, layerId, layer);

                if (feature == null) {
                    throw "No feature found";
                }
            }


            var geom = feature.getGeometry();

            if (scale == null && geom.getType() == 'Point') {
                scale = 500;
            }

            if (geom == null) {
                throw "Feature has no geometry";
            }

            if (scale == null || isNaN(scale) || scale < 0) {

                this.map.getView().fit(geom.getExtent(), { duration: 1000, padding: [50, 50, 50, 50] });
            }
            else {
                var resolution = getResolutionForScale(scale, this.map.getView().getProjection().getCode());
                var extent = geom.getExtent();
                var _x = extent[0] + (extent[2] - extent[0]) / 2;
                var _y = extent[1] + (extent[3] - extent[1]) / 2;
                var center = [_x, _y];
                this.map.getView().animate({
                    center: center,
                    resolution: resolution,
                    duration: 2000
                });
            }

            if (onSuccessCallback != null) {
                onSuccessCallback();
            }
        }
        catch (e) {
            if (onErrorCallback != null) {
                onErrorCallback(e);
            }
        }
    }

    centerByWkt(wkt, scale, projectionCode) {
        var format = new WKT();
        this.center(format.readFeature(wkt).getGeometry().getFlatCoordinates(), scale, projectionCode);
    }

    showFeatures(layerId, featureIds, scale, onSuccessCallback, onErrorCallback) {
        if (featureIds == null) {
            throw "No feature was requested";
        }

        if (!Array.isArray(featureIds)) {
            featureIds = [featureIds];
        }

        if (featureIds.length > 1) {
            this.selectionHandler.selectionHolder.allowMultiple = true;
        }

        try {
            $event.notify(eventTypes.LAYER_VISIBILITY_CHANGED, { layerId: layerId, visible: true }, this, this.classId);
            let layer = this.layerHandler.getLayerById(layerId)[0];
            let features = [];

            if (layer.getSource().strategy_ == allStrategy) {
                let idField = layer.get("idField");
                let all = layer.getSource().getFeatures();
                featureIds.forEach(featureId => {
                    let found = all.find(x => x.get(idField) == featureId);
                    if (found) {
                        features.push(found);
                        this.selectionHandler.select(layerId, featureId, true, false, true);
                    }
                });

                if (features.length == 0) {
                    throw "No feature found";
                }
            } else {
                this.refreshLayer(layerId);
                featureIds.forEach(featureId => {
                    this.selectionHandler.select(layerId, featureId, true, true, true);
                });

                features = this.selectionHandler.getSelectedFeaturesDetails(layerId, featureIds);
            }

            featureIds.forEach(featureId => {
                this.selectionHandler.notifySelection(featureId, layerId, layer);
            });

            let extent = createEmptyExtent();

            for (let i = 0; i < features.length; i++) {
                let geom = features[i].getGeometry();

                if (geom != null && geom.getExtent() != null) {
                    extendExtent(extent, geom.getExtent());
                }
            }

            if (scale == null || isNaN(scale) || scale < 0) {
                this.map.getView().fit(extent, { duration: 2000, padding: [50, 50, 50, 50] });
            }
            else {
                var resolution = getResolutionForScale(scale, this.map.getView().getProjection().getCode());
                var _x = extent[0] + (extent[2] - extent[0]) / 2;
                var _y = extent[1] + (extent[3] - extent[1]) / 2;
                var center = [_x, _y];
                this.map.getView().animate({
                    center: center,
                    resolution: resolution,
                    duration: 2000
                });
            }

            if (onSuccessCallback != null) {
                onSuccessCallback();
            }
        } catch (e) {
            if (onErrorCallback != null) {
                onErrorCallback(e);
            }
        }
    }

    init() {
        return new Promise((resolve, reject) => {
            let promises = [];
            this.resourcesHandler = new ResourcesHandler(this.rootURL);
            this.generalSettingsHandler = new GeneralSettingsHandler(this.rootURL, this.mapId);
            this.generalActionSettingsHandler = new GeneralActionSettingsHandler(this.rootURL, this.mapId);
            this.layerSettingsHandler = new LayerSettingsHandler(this.rootURL, this.mapId, this.resourcesHandler, this.classId);
            this.featureHandler = new FeatureHandler(this.generalSettingsHandler, this.layerSettingsHandler, this.rootURL, this);
            this.layerSettingsHandler.featureHandler = this.featureHandler;
            this.gisSettingsHandler = new GisSettingsHandler(this.rootURL);

            promises.push(this.resourcesHandler.load());
            promises.push(this.generalSettingsHandler.load());
            promises.push(this.generalActionSettingsHandler.load());
            promises.push(this.layerSettingsHandler.load());
            promises.push(this.gisSettingsHandler.load());
            this._showLoadingPanel();
            Promise.all(promises).then(() => {
                this._init().then(() => {
                    hideLoadingPanel();
                    this.loaded = true;

                    this.geolocationHandler = new GeolocationHandler(this.layerHandler);
                    this.geolocationHandler.init();

                    this.addTemporaryVectorLayer({
                        name: layerTypes.SEARCH,
                        geometryType: geomTypes.GEOM_ALL,
                        srid: this.generalSettingsHandler.settings.ProjCode,
                        geomField: 'geom',
                        idField: 'id',
                        visible: true,
                        ignoreStyle: true,
                        addToLegend: false,
                        showLegend: false,
                        declutter: false
                    });

                    $event.notify(eventTypes.MAP_LOADED, null, this, this.classId);

                    resolve();
                }).catch(err => {
                    hideLoadingPanel();
                });
            }).catch(err => {
                hideLoadingPanel();
            });
        });
    }

    deselectAll(layerId) {
        this.selectionHandler.deselectAll(layerId);
    }

    refreshLayer(layerId) {
        this.layerHandler.refreshLayer(layerId);
    }


    getFeatureDetails(layerUid, featureId) {
        try {
            return this.selectionHandler.getSelectedFeatureDetails(layerUid, featureId);
        }
        catch (e) {
            return null;
        }
    }

    getAllSelectedFeatureDetails(layerId) {
        try {
            return this.selectionHandler.getAllSelectedFeatureDetails(layerId);
        }
        catch (e) {
            return null;
        } 
    }

    removeFeatures(features) {
        this.featureHandler.removeFeatures(features);
    }

    getAllSelectedFeatures(layerId) {
        return this.selectionHandler.getAllSelectedFeatures(layerId);
    }

    getViewExtent() {
        return this.map.getView().getViewStateAndExtent().extent;
    }

    getViewCenter() {
        return this.map.getView().getCenter();
    }

    getViewScale() {
        if (this.map) {
            let resolution = this.map.getView().getResolution();
            return getScaleForResolution(resolution, this.generalSettingsHandler.settings.ProjCode);
        }
    }

    setProjection(projectionCode) {
        this.projectionHandler.load(projectionCode).then(() => {
            this.generalSettingsHandler.setProjection(projectionCode);
            this.refreshView();
        });
    }

    getViewProjection() {
        return this.map.getView().getProjection().getCode();
    }

    reprojectExtent(extent, initialProjection, targetProjection) {
        return transformExtent(extent, initialProjection, targetProjection);
    }

    reprojectCoordinate(coordinate, initialProjection, targetProjection) {
        return transform(coordinate, initialProjection, targetProjection);
    }

    transformCoordinates(coordinates, fromProjCode, toProjCode) {

        return transform(coordinates, fromProjCode, toProjCode);
    }

    center(pointCoordinates, scale, projectionCode) {
        if (pointCoordinates == null || pointCoordinates.length != 2) {
            return;
        }

        if (projectionCode != null && projectionCode != this.map.getView().getProjection().getCode()) {
            pointCoordinates = transform(pointCoordinates, projectionCode, this.map.getView().getProjection().getCode());
        }

        var resolution = this.map.getView().get('resolution');
        if (scale != null && !isNaN(scale) && scale > 0) {
            resolution = getResolutionForScale(scale, this.map.getView().getProjection().getCode());
        }

        this.map.getView().animate({
            center: pointCoordinates,
            resolution: resolution,
            duration: 500
        });
    }

    refreshView() {
        let options = {};
        let projExtent = this.projectionHandler.getProjExtent(this.generalSettingsHandler.settings.ProjCode);

        addOption(options, 'extent', projExtent);
        //addOption(options, "minResolution", getResolutionForScale(null, this.generalSettingsHandler.settings.ProjCode));
        //addOption(options, "maxResolution", getResolutionForScale(null, this.generalSettingsHandler.settings.ProjCode));
        let oldProj = this.getViewProjection();
        let newProj = this.generalSettingsHandler.settings.ProjCode;
        let center = this.getViewCenter();
        let oldProjection = this.projectionHandler.getProj(this.getViewProjection());
        let newProjection = this.projectionHandler.getProj(this.generalSettingsHandler.settings.ProjCode);
        let newCenter = transform(center, oldProjection, newProjection);

        addOption(options, 'center', newCenter);

        let resolution = this.map.getView().getResolution();
        let oldMPU = oldProjection.getMetersPerUnit();
        let newMPU = newProjection.getMetersPerUnit();
        let oldPR = getPointResolution(oldProjection, 1 / oldMPU, center, 'm') * oldMPU;
        let newPR = getPointResolution(newProjection, 1 / newMPU, newCenter, 'm') * newMPU;
        let newResolution = (resolution * oldPR) / newPR;

        addOption(options, "resolution", newResolution);
        addOption(options, 'projection', this.projectionHandler.getProj(this.generalSettingsHandler.settings.ProjCode));
        addOption(options, "zoom", 1);

        let view = new View(
            options
        );

        view.on('change:resolution', () => {
            var scale = this.getViewScale();

            if (scale) {
                $event.notify(eventTypes.SCALE_CHANGED, {
                    scale: '1 : ' + parseInt(scale).toLocaleString()
                }, this, this.classId);
            }
        });

        this.map.setView(view);
        this.layerHandler.handleProjectionChange(oldProj, newProj);
    }

    disableViewExtentAndScale(keepExtent) {
        let options = {};
        let projExtent = this.projectionHandler.getProjExtent(this.generalSettingsHandler.settings.ProjCode);

        addOption(options, 'extent', keepExtent ? this.getViewExtent() : projExtent);
        addOption(options, "minResolution", getResolutionForScale(null, this.generalSettingsHandler.settings.ProjCode));
        addOption(options, "maxResolution", getResolutionForScale(null, this.generalSettingsHandler.settings.ProjCode));
        addOption(options, 'center', this.getViewCenter());
        addOption(options, "resolution", this.map.getView().getResolution());
        addOption(options, 'projection', this.projectionHandler.getProj(this.generalSettingsHandler.settings.ProjCode));
        addOption(options, "zoom", 1);

        let view = new View(
            options
        );

        view.on('change:resolution', () => {
            var scale = this.getViewScale();

            if (scale) {
                $event.notify(eventTypes.SCALE_CHANGED, {
                    scale: '1 : ' + parseInt(scale).toLocaleString()
                }, this, this.classId);
            }
        });

        this.map.setView(view);
    }

    resetViewExtent() {
        if (this.generalSettingsHandler.settings.ProjCode != this.getViewProjection()) {
            this.layerHandler.handleProjectionChange(this.getViewProjection(), this.generalSettingsHandler.settings.ProjCode);
        }
        this.map.setView(this._initView());
    }

    _clearEvents() {
        $event.clearReference(this.classId);
    }

    _showLoadingPanel() {
        Swal.fire({
            target: this.container,
            allowOutsideClick: false,
            onBeforeOpen: () => {
                Swal.showLoading();
                $('body').css('overflow', 'auto');
            },
            customClass: {
                container: 'position-absolute'
            }
        });
    }

    fitFeature(layerId, featureId) {
        return this.layerHandler.fitFeature(layerId, featureId);
    }

    fitWktGeometry(wkt, scale, featureProjection, dataProjection) {
        this.layerHandler.fitWktGeometry(wkt, scale, featureProjection, dataProjection);
    }

    clearSelection() {
        this.layerHandler.clearSelection();
    }

    fitTemporaryFeature(layerId, featureId) {
        return this.layerHandler.fitTemporaryFeature(layerId, featureId);
    }

    load(resourcesHandler, generalSettingsHandler, layerSettingsHandler, generalActionSettingsHandler, gisSettingsHandler) {
        return new Promise((resolve, reject) => {
            this.resourcesHandler = resourcesHandler;
            this.generalSettingsHandler = generalSettingsHandler;
            this.layerSettingsHandler = layerSettingsHandler;
            this.generalActionSettingsHandler = generalActionSettingsHandler;
            this.gisSettingsHandler = gisSettingsHandler;
            showLoadingPanel();
            this._init().then(() => {
                hideLoadingPanel();
                resolve();
            });
        });
    }

    clearSelection() {
        this.selectionHandler.clearSelection();
    }

    addTemporaryVectorLayer(options) {
        this.clearSelection();
        return this.layerHandler.addTemporaryVectorLayer(options);
    }

    addTemporaryFeatures(layerId, features, format, clearPrevious, fit) {
        return this.layerHandler.addTemporaryFeatures(layerId, features, format, clearPrevious, fit);
    }

    removeTemporaryFeatures(layerId, features) {
        return this.layerHandler.removeTemporaryFeatures(layerId, features);
    }

    clearTemporaryFeatures(layerId) {
        return this.layerHandler.clearTemporaryFeatures(layerId);
    }

    getFeatureById(layerId, featureId) {
        return this.layerHandler.getFeatureById(layerId, featureId);
    }

    _init() {
        return new Promise((resolve, reject) => {
            this.container.append(this._getMapTemplate());
            this._loadAdditionalConfig().then(() => {
                this._registerNotificationHandlers();
                resolve();
            });
        });
    }

    _loadAdditionalConfig() {
        return new Promise((resolve, reject) => {
            let promises = [];

            if (this.generalSettingsHandler.settings.ProjCode == null || this.generalSettingsHandler.settings.ProjCode.trim() == '') {
                this.generalSettingsHandler.settings.ProjCode = defaults.DEFAULT_SRID;
            }

            promises.push(this.projectionHandler.load(this.generalSettingsHandler.settings.ProjCode));
            Promise.all(promises).then(() => {
                this.map = this._initMap();
                this.selectionHandler = new SelectionHandler(this.map, this.layerSettingsHandler, this.generalSettingsHandler, this.mapId, this.classId, this.featureHandler, this.resourcesHandler);
                this.selectionHandler.init();

                this.layerHandler = new LayerHandler(this.mapId, this.map, this.layerSettingsHandler, this.projectionHandler, this.generalSettingsHandler, this.gisSettingsHandler, this.selectionHandler, this.rootURL, this.classId, this);

                this.generalActionHandler = new GeneralActionHandler(this.generalActionSettingsHandler, this.resourcesHandler, this.layerSettingsHandler, this.container, this.mapId, this.rootURL, this.classId, this.layerHandler.styleHandler, this);
                this.generalActionHandler.init();

                this.featureActionHandler = new FeatureActionHandler(this, this.classId, this.container, this.generalSettingsHandler, this.rootURL, this.resourcesHandler, this.generalActionHandler.addLayers, this.generalActionHandler.generalActionControls.editControl, this.generalActionHandler.multipleAddLayers);
                this.featureActionHandler.init();

                this.layerHandler.init().then(() => {
                    // TO DO: de venit cu o idee mai buna
                    //this._animate(this.map);
                    resolve();
                });

                this.temporaryEditHandler = new TemporaryEditHandler(this.mapId, this.map, this.layerHandler, this.selectionHandler, this.classId);
            });
        });
    }

    _animate(map) {
        map.render();
        requestAnimationFrame(() => this._animate(map));
    }

    _registerNotificationHandlers() {
        $event.register(eventTypes.LAYER_OPACITY_CHANGED, this._getLayerOpacityChangedHandler(), this, this.classId);
        $event.register(eventTypes.LAYER_VISIBILITY_CHANGED, this._getLayerVisibilityChangedHandler(), this, this.classId);
        $event.register(eventTypes.LAYER_CLASSIFICATION_VISIBILITY_CHANGED, this._getLayerClassificationVisibilityChangedHandler(), this, this.classId);
        $event.register(eventTypes.LAYER_ORDER_CHANGED, this._getLayerOrderChangedHandler(), this, this.classId);
        $event.register(eventTypes.LAYER_REMOVED, this._getLayerRemovedHandler(), this, this.classId);
    }

    _getLayerOpacityChangedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null) {
                this.layerHandler.changeLayerOpacity(data.layerId, data.opacity);
            }
        };

        return handler;
    }

    _getLayerClassificationVisibilityChangedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null) {
                this.layerHandler.changeLayerClassificationVisibility(data.layerId, data.classificationIdx, data.visible);
            }
        };

        return handler;
    }

    _getLayerVisibilityChangedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null) {
                this.layerHandler.changeLayerVisibility(data.layerId, data.visible);
            }
        };

        return handler;
    }

    _getLayerOrderChangedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null) {
                this.layerHandler.changeLayersOrder(data.layers, true);
            }
        };

        return handler;
    }

    getLayerSettings(layerId) {
        return this.layerHandler.getLayerSettings(layerId);
    }

    getLayer(layerId) {
        let layers = this.layerHandler.getLayerById(layerId);

        if (layers != null && layers.length > 0) {
            return layers[0];
        }
        return null;
    }

    getLayerStyle(layerId) {
        return this.layerHandler.getLayerStyle(layerId);
    }

    selectFeature(layerId, featureId, initialized, ignoreNotification, force) {
        this.selectionHandler.select(layerId, featureId, initialized, ignoreNotification, force);
    }

    deselectFeature(layerId, featureId) {
        this.selectionHandler.deselectFeature(layerId, featureId);
    }

    _getLayerRemovedHandler() {
        var handler = (eventName, data, sender) => {
            if (data != null) {
                this.layerHandler.removeLayers(data.ids);
            }
        };

        return handler;
    }

    _initMap() {
        let view = this._initView();
        let map = new Map({
            layers: [],
            target: document.getElementById(this.mapElementId),
            view: view
        });

        return map;
    }

    setScale(scale) {
        this.map.getView().setResolution(getResolutionForScale(scale, this.generalSettingsHandler.settings.ProjCode));
    }

    _initView() {
        let options = {};

        addOption(options, 'extent', this._getMapDefaultExtent());
        addOption(options, "minResolution",
            getResolutionForScale(this.generalSettingsHandler.settings.MinScale, this.generalSettingsHandler.settings.ProjCode));
        addOption(options, "maxResolution",
            getResolutionForScale(this.generalSettingsHandler.settings.MaxScale, this.generalSettingsHandler.settings.ProjCode));
        addOption(options, 'center', this._getMapDefaultCenterPoint());
        addOption(options, "resolution",
            getResolutionForScale(this.generalSettingsHandler.settings.DefaultScale, this.generalSettingsHandler.settings.ProjCode));
        addOption(options, 'projection', this.projectionHandler.getProj(this.generalSettingsHandler.settings.ProjCode));
        addOption(options, "zoom", 1);

        let view = new View(
            options
        );

        view.on('change:resolution', () => {
            var scale = this.getViewScale()

            if (scale) {
                $event.notify(eventTypes.SCALE_CHANGED, {
                    scale: '1 : ' + parseInt(scale).toLocaleString()
                }, this, this.classId);
            }
        });

        return view;
    }

    _getMapDefaultExtent() {
        let xMin = this.generalSettingsHandler.settings.XMin;
        let yMin = this.generalSettingsHandler.settings.YMin;
        let xMax = this.generalSettingsHandler.settings.XMax;
        let yMax = this.generalSettingsHandler.settings.YMax;

        if (xMin != null && yMin != null && xMax != null && yMax != null) {
            return [xMin, yMin, xMax, yMax];
        }

        return this.projectionHandler.getProjExtent(this.generalSettingsHandler.settings.ProjCode);
    }

    _getMapDefaultCenterPoint() {

        if (this.generalSettingsHandler.settings.DefaultCenterPointX != null && this.generalSettingsHandler.settings.DefaultCenterPointY != null) {
            return [this.generalSettingsHandler.settings.DefaultCenterPointX, this.generalSettingsHandler.settings.DefaultCenterPointY];
        }

        let extent = this._getMapDefaultExtent();
        return getExtentCenter(extent);
    }

    _getMapTemplate() {
        let div = document.createElement('div');
        div.innerHTML = `<div class="map" id=` + this.mapElementId + `>
                        </div>`;
        return div.firstChild;
    }
}